import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Modal,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  ModalFooter,
  ModalBody,
  Text,
  useDisclosure,
  useClipboard,
  useMediaQuery,
  useTheme,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  IconButton,
} from '@chakra-ui/react';
import memberReferralService from '../../../../../service/memberReferralService';
import pnotifyService from '../../../../../service/pnotifyService';
import {
  User as UserIcon,
  Link as LinkIcon,
} from '../../../../context/Chakra/Icons';
import { translateModel } from '../../../../../utils/translate';
import { SECTION_STATUS } from '../../../constants';
import { useAppContext } from '../../../context/appContext';
import ErrorPanel from '../components/ErrorPanel';
import Loading from '../components/Loading';
import { useProfileContext } from '../context/profileContext';
import { EVENT_FOR_MEMBER_CENTER } from '../../../../../shop/constants/tracker';
import trackerService from '../../../../../service/trackerService';

const Content = () => {
  const { merchantData } = useAppContext();
  const { t, i18n } = useTranslation();
  const { userData } = useProfileContext();
  const theme = useTheme();
  const [isDesktop] = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const [campaign, setCampaign] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [status, setStatus] = useState(SECTION_STATUS.INITIAL);
  const [referralCode, setReferralCode] = useState(userData.referral_code);
  const [hasTrackedRefShow, setHasTrackedRefShow] = useState(false);
  const url = `${window.location.origin}/?sl-mref=${referralCode}`;
  const { hasCopied, onCopy } = useClipboard(url);
  const blockRef = useRef(null);

  const getCampaign = async () => {
    try {
      setStatus(SECTION_STATUS.LOADING);
      const response = await memberReferralService.getReferralCampaigns(
        merchantData._id,
      );
      setStatus(SECTION_STATUS.SUCCESS);
      if (response.data.total === 1) {
        setCampaign(response.data.campaigns[0]);
      }
    } catch (error) {
      setStatus(SECTION_STATUS.FAILURE);
      console.error(error);
    }
  };

  const generateCode = async () => {
    try {
      const response = await memberReferralService.generateReferralCode();
      setReferralCode(response.data.user.referral_code);
      pnotifyService.notify(
        t('member_profile.member_referral.generate.success'),
      );
    } catch (error) {
      setStatus(SECTION_STATUS.FAILURE);
      console.error(error);
    }
  };

  const handleCopy = () => {
    onCopy();
    trackerService.track({
      type: EVENT_FOR_MEMBER_CENTER.MEMBER_REFERRAL_LINK_COPY,
      data: {
        event_category: 'MemberReferral',
      },
    });
  };

  useEffect(() => {
    getCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasCopied) {
      pnotifyService.notify(t('member_profile.member_referral.copy.success'));
    }
  }, [hasCopied, t]);

  useEffect(() => {
    if (
      hasTrackedRefShow ||
      !campaign ||
      !blockRef.current ||
      !('IntersectionObserver' in window)
    )
      return;

    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(
      ([entry], obs) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          trackerService.track({
            type: EVENT_FOR_MEMBER_CENTER.MEMBER_REFERRAL_LINK_SHOW,
            data: {
              link_type: referralCode ? 'copy' : 'generate',
              event_category: 'MemberReferral',
            },
          });
          setHasTrackedRefShow(true);
          obs.unobserve(entry.target);
        }
      },
      { threshold: [0.5] },
    );

    observer.observe(blockRef.current);

    return () => observer.disconnect();
  }, [referralCode, campaign, hasTrackedRefShow]);

  if (status === SECTION_STATUS.LOADING) {
    return <Loading />;
  }
  if (status === SECTION_STATUS.FAILURE) {
    return <ErrorPanel onReload={getCampaign} hideErrorImg />;
  }
  if (!campaign) {
    return null;
  }
  return (
    <>
      <Flex
        ref={blockRef}
        gap="10px"
        direction="column"
        data-e2e-id="member-center-profile-referral_container"
        w={isDesktop ? '50%' : '100%'}
      >
        <Flex
          gap="14px"
          alignItems="center"
          className="member-center-profile-section_title"
        >
          <UserIcon
            className="member-referral_user-icon member-center-profile__section-title-icon"
            color="#dddddd"
            fontSize="20px"
          />
          <Text
            variant="sectionTitle"
            className="member-center-profile__section-title-text"
          >
            {t('member_profile.member_referral.discount')}
          </Text>
        </Flex>
        <Flex
          direction={referralCode ? 'column' : 'row'}
          alignItems={referralCode ? 'flex-start' : 'center'}
          gap="10px"
        >
          <Flex flex="1">
            <Text
              textAlign="left"
              variant="description"
              className="member-center-profile__description-text member-referral-rule__text"
            >
              {`${translateModel(campaign.name_translations, i18n.language)}: 
            ${t('member_profile.member_referral.code.copy_and_share')}`}
              {referralCode ? (
                <Button
                  ml="4px"
                  variant="textButton"
                  className="member-center-profile__text-button member-referral-rule__button"
                  onClick={onOpen}
                  minHeight="auto"
                  verticalAlign="baseline"
                  lineHeight="20px"
                >
                  {t('member_profile.member_referral.code.remarks')}
                </Button>
              ) : (
                <Popover placement="right">
                  <PopoverTrigger>
                    <IconButton
                      icon={
                        <i className="fa fa-lg fa-question-circle birthday-faq" />
                      }
                      minHeight="auto"
                      backgroundColor="transparent"
                      _hover={{
                        backgroundColor: 'transparent',
                      }}
                      _active={{
                        backgroundColor: 'transparent',
                      }}
                    />
                  </PopoverTrigger>
                  <Portal>
                    <PopoverContent
                      _focus={{
                        boxShadow: 'none',
                      }}
                    >
                      <PopoverBody>
                        {translateModel(
                          campaign.remarks_translations,
                          i18n.language,
                        )}
                      </PopoverBody>
                    </PopoverContent>
                  </Portal>
                </Popover>
              )}
            </Text>
          </Flex>
          {referralCode ? (
            <Flex
              w="full"
              direction={isDesktop ? 'row' : 'column'}
              gap="8px"
              alignItems={isDesktop ? 'center' : 'flex-end'}
            >
              <Box
                w="full"
                flex={1}
                border="1px solid #DDDDDD"
                borderRadius="4px"
                p="7px 10px"
                textAlign="start"
                whiteSpace="nowrap"
                overflow="scroll"
                css={{
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                }}
              >
                <LinkIcon mr="10px" />
                {url}
              </Box>
              <Button
                variant="textButton"
                className="member-center-profile__submit-button member-center-profile__primary-button btn btn-primary btn-color-primary"
                onClick={handleCopy}
                verticalAlign="top"
                data-e2e-id="member-center-profile-referral-copy_button"
                w="80px"
              >
                {t('member_profile.member_referral.copy')}
              </Button>
            </Flex>
          ) : (
            <Button
              variant="textButton"
              className="member-center-profile__submit-button member-center-profile__primary-button btn btn-primary btn-color-primary"
              onClick={generateCode}
              minHeight="auto"
              data-e2e-id="member-center-profile-referral-gen-link_button"
            >
              {t('member_profile.member_referral.generate')}
            </Button>
          )}
        </Flex>
      </Flex>
      <Modal
        isOpen={isOpen}
        onOverlayClick={onClose}
        autoFocus={false}
        size="4xl"
        scrollBehavior="inside"
        isCentered
      >
        <ModalOverlay />
        <ModalContent
          bgColor="primary.500"
          borderRadius="6px"
          mx="4"
          color="#5b5b5b"
          className="member-center-profile__member-referral-modal"
        >
          <ModalHeader
            className="member-center-profile__member-referral-modal-text"
            fontSize="16px"
          >
            {t('member_profile.member_referral.rules')}
          </ModalHeader>
          <ModalBody
            variant="default"
            className="member-center-profile__member-referral-modal-text"
            fontSize="14px"
            whiteSpace="pre-line"
          >
            {translateModel(campaign.remarks_translations, i18n.language)}
          </ModalBody>
          <ModalFooter>
            <Button
              onClick={onClose}
              variant="fill"
              autoFocus={false}
              className="member-center-profile__member-referral-modal-button"
            >
              {t('promotions.page.term.confirm')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const MemberReferral = () => {
  const { slFeatureService } = useAppContext();
  const hasMemberReferral = slFeatureService.hasFeature('customer_referral');
  if (!hasMemberReferral) {
    return null;
  }
  return <Content />;
};

export default MemberReferral;
